import React from "react";
import {
  FileText,
  Phone,
  ExternalLink,
  Check,
  X,
  Search,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Badge } from "../ui/badge";
import { cn } from "../../lib/utils";

const MAX_SELECTION = 5;

// ── Metric normalization ─────────────────────────────────────────────────────
// Runs (render json: runs) carry `metric_results_data` — an array of
//   { metric_name, status, result_type, result: { value } }.
// Observations (list endpoint) carry `metric_values` — a map
//   { metric_id: { metric_name, value, status, result_type } }.
// Normalize both to { name, value, status }.
const normalizeRunMetrics = (arr) =>
  (Array.isArray(arr) ? arr : []).map((e) => ({
    name: e.metric_name || e.name,
    value: e?.result?.value ?? e.value,
    status: e.status,
  }));

const normalizeObsMetrics = (map) =>
  Object.values(map || {}).map((e) => ({
    name: e.metric_name,
    value: e.value,
    status: e.status,
  }));

const isComputed = (m) => m.status == null || m.status === "computed";
const isFail = (m) => {
  if (!isComputed(m)) return false;
  const v = m.value;
  if (v === false) return true;
  if (typeof v === "string") return ["fail", "failed", "false", "no"].includes(v.toLowerCase());
  return false;
};
const isPass = (m) => {
  if (!isComputed(m)) return false;
  const v = m.value;
  if (v === true) return true;
  if (typeof v === "string") return ["pass", "passed", "true", "yes", "success"].includes(v.toLowerCase());
  return false;
};
const failedCount = (metrics) => (metrics || []).filter(isFail).length;

const fmtDuration = (secs) => {
  const s = Number(secs);
  if (!Number.isFinite(s) || s <= 0) return "—";
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return `${m}:${String(r).padStart(2, "0")}`;
};

const fmtWhen = (ts) => {
  if (!ts) return "—";
  try {
    return formatDistanceToNow(new Date(ts), { addSuffix: true });
  } catch {
    return "—";
  }
};

function MetricsCell({ metrics }) {
  if (!metrics || metrics.length === 0) {
    return <span className="text-xs text-muted-foreground">Not analyzed</span>;
  }
  const failed = metrics.filter(isFail);
  const passed = metrics.filter(isPass);
  const hasPassFail = passed.length + failed.length > 0;
  return (
    <div className="flex flex-col gap-1">
      {hasPassFail ? (
        <div className="flex items-center gap-3 text-xs">
          <span className="inline-flex items-center gap-1 text-emerald-600">
            <Check className="w-3.5 h-3.5" /> {passed.length} passed
          </span>
          <span className="inline-flex items-center gap-1 text-destructive">
            <X className="w-3.5 h-3.5" /> {failed.length} failed
          </span>
        </div>
      ) : (
        <span className="text-xs text-muted-foreground">
          {metrics.length} metric{metrics.length !== 1 ? "s" : ""} evaluated
        </span>
      )}
      {failed.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {failed.slice(0, 4).map((m, idx) => (
            <Badge key={idx} variant="destructive">
              {m.name || "metric"}
            </Badge>
          ))}
          {failed.length > 4 && (
            <span className="text-[11px] text-muted-foreground">+{failed.length - 4} more</span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * CallPicker — sortable/filterable table selector for choosing up to 5 calls.
 * Selections persist across BOTH sources; the radio only switches the list.
 */
export default function CallPicker({
  runs = [],
  observations = [],
  value = [],
  onChange,
  source = "runs",
}) {
  const [activeSource, setActiveSource] = React.useState(source);
  const [filterText, setFilterText] = React.useState("");
  const [sortConfig, setSortConfig] = React.useState(null);

  const isSelected = (type, id) => value.some((i) => i.type === type && i.id === id);

  const handleToggle = (type, id) => {
    if (isSelected(type, id)) {
      onChange(value.filter((i) => !(i.type === type && i.id === id)));
      return;
    }
    if (value.length >= MAX_SELECTION) return; // at cap
    onChange([...value, { type, id }]);
  };

  const atCap = value.length >= MAX_SELECTION;
  const runCount = value.filter((i) => i.type === "Run").length;
  const obsCount = value.filter((i) => i.type === "CallObservation").length;

  const baseRows =
    activeSource === "runs"
      ? runs.map((r) => ({
          type: "Run",
          id: r.id,
          idLabel: `Run #${r.id}`,
          subtitle: r.scenario_id ? `Scenario ${r.scenario_id}` : null,
          duration: r.duration,
          when: r.created_at,
          metrics: normalizeRunMetrics(r.metric_results_data),
          href: r.batch_id ? `/batch-run/${r.batch_id}/run/${r.id}` : null,
        }))
      : observations.map((o) => ({
          type: "CallObservation",
          id: o.id,
          idLabel: o.call_identifier || `Call #${o.id}`,
          subtitle: o.call_end_reason || null,
          duration: o.call_duration ?? o.duration,
          when: o.call_started_at || o.created_at,
          metrics: normalizeObsMetrics(o.metric_values),
          href: `/observability/conversations/${o.id}`,
        }));

  const sortValue = (row, key) => {
    switch (key) {
      case "duration": return Number(row.duration) || -1;
      case "when": return row.when ? new Date(row.when).getTime() : 0;
      case "failed": return failedCount(row.metrics);
      case "id":
      default: return Number(row.id) || 0;
    }
  };

  const rows = React.useMemo(() => {
    const q = filterText.trim().toLowerCase();
    let out = baseRows;
    if (q) {
      out = out.filter(
        (r) =>
          r.idLabel.toLowerCase().includes(q) ||
          (r.subtitle && r.subtitle.toLowerCase().includes(q)) ||
          String(r.id).includes(q)
      );
    }
    if (sortConfig?.key) {
      out = [...out].sort((a, b) => {
        const va = sortValue(a, sortConfig.key);
        const vb = sortValue(b, sortConfig.key);
        if (va < vb) return sortConfig.direction === "asc" ? -1 : 1;
        if (va > vb) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return out;
  }, [baseRows, filterText, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (!prev || prev.key !== key) return { key, direction: "asc" };
      if (prev.direction === "asc") return { key, direction: "desc" };
      return null;
    });
  };

  const SortHeader = ({ colKey, label, className }) => {
    const sorted = sortConfig?.key === colKey;
    return (
      <th className={cn("px-3 py-2 font-medium", className)}>
        <button
          type="button"
          onClick={() => handleSort(colKey)}
          className="inline-flex items-center gap-1 uppercase tracking-wide text-[11px] text-muted-foreground hover:text-foreground transition-colors"
        >
          {label}
          {sorted ? (
            sortConfig.direction === "asc" ? (
              <ArrowUp className="h-3 w-3" />
            ) : (
              <ArrowDown className="h-3 w-3" />
            )
          ) : (
            <ArrowUpDown className="h-3 w-3 text-muted-foreground/40" />
          )}
        </button>
      </th>
    );
  };

  const SourceRadio = ({ id, icon: Icon, label, count }) => (
    <label
      className={cn(
        "flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer text-sm transition-colors",
        activeSource === id
          ? "border-primary bg-primary/5 text-foreground"
          : "border-border text-muted-foreground hover:border-input"
      )}
    >
      <input
        type="radio"
        name="call-picker-source"
        checked={activeSource === id}
        onChange={() => setActiveSource(id)}
        className="accent-primary"
      />
      <Icon className="w-4 h-4" />
      <span className="font-medium">{label}</span>
      <span className="text-xs text-muted-foreground">({count})</span>
    </label>
  );

  const empty = rows.length === 0;

  return (
    <div className="flex flex-col gap-3">
      {/* Source radios + combined counter */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-2">
          <SourceRadio id="runs" icon={FileText} label="Batch Runs" count={runs.length} />
          <SourceRadio id="observations" icon={Phone} label="Call Observations" count={observations.length} />
        </div>
        <span
          className={cn(
            "text-xs font-medium tabular-nums",
            atCap ? "text-destructive" : "text-muted-foreground"
          )}
        >
          {value.length} / {MAX_SELECTION} selected
          {value.length > 0 && (
            <span className="ml-1 text-muted-foreground font-normal">
              ({runCount} run{runCount !== 1 ? "s" : ""} · {obsCount} call{obsCount !== 1 ? "s" : ""})
            </span>
          )}
        </span>
      </div>

      {/* Filter box */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          placeholder={`Filter ${activeSource === "runs" ? "runs" : "calls"} by ID…`}
          className="w-full pl-9 pr-3 py-2 border border-input rounded-md text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
        />
      </div>

      {/* Table */}
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="max-h-[480px] overflow-y-auto">
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 z-10 bg-muted">
              <tr className="text-left">
                <th className="w-10 px-3 py-2"></th>
                <SortHeader colKey="id" label={activeSource === "runs" ? "Run" : "Call"} />
                <SortHeader colKey="duration" label="Duration" className="w-24" />
                <SortHeader colKey="when" label="When" className="w-32" />
                <SortHeader colKey="failed" label="Metrics" />
                <th className="px-3 py-2 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {empty ? (
                <tr>
                  <td colSpan={6} className="text-center py-10 text-sm text-muted-foreground">
                    {filterText.trim()
                      ? "No calls match your filter."
                      : activeSource === "runs"
                      ? "No batch runs available."
                      : "No call observations available."}
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const selected = isSelected(row.type, row.id);
                  const disabled = atCap && !selected;
                  return (
                    <tr
                      key={`${row.type}-${row.id}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => !disabled && handleToggle(row.type, row.id)}
                      onKeyDown={(e) => {
                        if ((e.key === "Enter" || e.key === " ") && !disabled) {
                          e.preventDefault();
                          handleToggle(row.type, row.id);
                        }
                      }}
                      className={cn(
                        "border-t border-border align-top transition-colors",
                        selected
                          ? "bg-primary/5"
                          : disabled
                          ? "opacity-50 cursor-not-allowed bg-muted/30"
                          : "hover:bg-muted/40 cursor-pointer"
                      )}
                    >
                      <td className="px-3 py-2.5">
                        <input
                          type="checkbox"
                          checked={selected}
                          disabled={disabled}
                          aria-label={row.idLabel}
                          tabIndex={-1}
                          readOnly
                          className="mt-0.5 accent-primary pointer-events-none"
                        />
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="font-medium text-foreground">{row.idLabel}</div>
                        {row.subtitle && (
                          <div className="text-xs text-muted-foreground">{row.subtitle}</div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 tabular-nums text-foreground">{fmtDuration(row.duration)}</td>
                      <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{fmtWhen(row.when)}</td>
                      <td className="px-3 py-2.5">
                        <MetricsCell metrics={row.metrics} />
                      </td>
                      <td className="px-3 py-2.5">
                        {row.href && (
                          <a
                            href={row.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline whitespace-nowrap"
                          >
                            View call <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
